import { Popover } from 'react-tiny-popover';
import { useEffect, useMemo, useState } from 'react';
import { fileIcons } from '../../consts/fileTypes';
import { IoIosArrowDown } from 'react-icons/io';
import { useDebounce } from '../../hooks/useDebounce';
import { Box, Button, Flex, Text } from '@mantine/core';
import { TbPlus } from 'react-icons/tb';
import React from 'react';

interface FileOption {
  name: string;
  value: string;
  format?: string;
  render?: (props: { onClick: () => void }) => React.ReactNode;
}

interface SelectedFile {
  name?: string;
  value?: string;
  format?: string;
}

interface FileSelectProps extends React.HTMLAttributes<HTMLDivElement> {
  hasIcon?: boolean;
  options?: FileOption[];
  selectedFile?: SelectedFile;
  variant?: string;
  onSelectOption: (option: FileOption) => void;
  children?: React.ReactNode;
  onCreateNewFund: (fundName: string) => void;
  onUpdateFund: (value: string, name: string) => void;
  isSourceFund?: boolean;
  isCreateLoading?: boolean;
  readOnly?: boolean;
}

export default function FileSelect({
  hasIcon = true,
  options,
  selectedFile,
  variant,
  onSelectOption,
  children,
  onCreateNewFund,
  onUpdateFund,
  isSourceFund = false,
  isCreateLoading,
  readOnly = false,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const toggle = () => setIsOpen(prev => !prev);

  const Icon = fileIcons[selectedFile?.format || 'xlsx'];

  const [search, setSearch] = useState('');
  const debouncedName = useDebounce(value, 600);
  const onChange = e => {
    setSearch(e.target.value);
    setIsOpen(true);
    if (selectedFile?.value) setValue(e.target.value);
  };

  useEffect(() => {
    if (debouncedName && isSourceFund && selectedFile?.value) {
      onUpdateFund(selectedFile?.value, debouncedName);
    }
  }, [debouncedName]);

  useEffect(() => {
    if (selectedFile?.name && isSourceFund) {
      setSearch(selectedFile.name);
    }
  }, [selectedFile?.name, isSourceFund]);

  const filteredOptions = useMemo(() => {
    if (options) {
      let _options = options;
      if (search && search !== selectedFile?.name)
        _options = options?.filter(el => el?.name?.toLowerCase()?.includes(search?.toLowerCase()));
      return _options;
    }

    return [];
  }, [search, options]);

  return (
    <Box w="100%">
      <Popover
        padding={5}
        isOpen={isOpen}
        onClickOutside={toggle}
        positions={['bottom', 'top', 'left', 'right']}
        //@ts-ignore
        transitionDuration={300}
        // The popover portals to body; without an explicit z-index it lands
        // below host overlays like FundMappingModal's drawer (zIndex: 99999),
        // so the content renders but is hidden behind it.
        containerStyle={{ zIndex: '100000' }}
        content={
          <Box
            mah="300px"
            pos="relative"
            w="262px"
            bg="#fff"
            p="4px"
            //@ts-ignore
            sx={() => ({
              borderRadius: '4px',
              border: '1px solid #ced4da',
              boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.05)',
              overflowY: 'auto'
            })}
          >
            <Flex
              align="center"
              // @ts-ignore
              sx={() => ({
                cursor: 'pointer'
              })}
              w="100%"
              h="32px"
              p="0 8px"
              maw="300px"
            >
              <Button
                disabled={!search}
                onClick={() => onCreateNewFund(search)}
                loading={isCreateLoading}
                leftSection={<TbPlus />}
                variant="transparent"
                w="100%"
                size="sm"
                display="flex"
                justify="flex-start"
                bg="#fff"
                p="0"
              >
                Add New Fund
              </Button>
            </Flex>
            {filteredOptions?.length === 0 && (
              <Flex align="center" w="100%" h="32px" p="0 8px" maw="300px">
                <Text ta="center" c="#ADB5BD" fz="14px" lh="20px">
                  No funds found
                </Text>
              </Flex>
            )}
            {filteredOptions?.map((option, idx) => (
              <Flex
                align="center"
                w="100%"
                h="32px"
                p="0 8px"
                maw="300px"
                // @ts-ignore
                sx={() => ({
                  cursor: 'pointer'
                })}
                key={idx}
                onClick={() => {
                  if (!option?.render) onSelectOption(option);
                  toggle();
                }}
              >
                {hasIcon && <Icon />}
                {option?.render ? (
                  option.render({ onClick: onCreateNewFund(search) })
                ) : (
                  <Text truncate fz="14px" lh="20px">
                    {option?.name}
                  </Text>
                )}
              </Flex>
            ))}
          </Box>
        }
      >
        <Flex
          h="43px"
          fz="16px"
          lh="20px"
          fw="500"
          ta="left"
          w="100%"
          p="0 12px"
          align="center"
          gap="8px"
          justify="space-between"
          // @ts-ignore
          sx={() => ({
            borderRadius: '4px'
          })}
          {...props}
          variant={variant}
          onClick={readOnly ? () => {} : toggle}
          style={{
            background: readOnly ? '#F1F3F5' : '#fff',
            border: readOnly ? '1px solid #fff' : '1px solid #ADB5BD'
          }}
        >
          <Flex align="center" gap="8px" h="100%" w="100%">
            {hasIcon && <Icon />}

            {isSourceFund ? (
              <input
                placeholder="Select Fund"
                onChange={onChange}
                onClick={e => {
                  e.stopPropagation();
                  if (!readOnly) setIsOpen(true);
                }}
                value={search}
                readOnly={readOnly}
                style={{
                  background: readOnly ? '#F1F3F5' : '#fff',
                  border: '1px solid transparent',
                  outline: 'none',
                  width: '100%',
                  height: '100%',
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#000',
                  // @ts-ignore
                  '&::placeholder': {
                    color: '#adb5bd'
                  },
                  textOverflow: 'ellipsis'
                }}
              />
            ) : (
              <Text fz="14px" lh="20px" fw="500" c="#000">
                {selectedFile?.name}
              </Text>
            )}
          </Flex>
          <Flex align="center" gap="6px">
            {children}

            {isSourceFund && !readOnly && (
              <IoIosArrowDown color="#868e96" style={{ minWidth: '16px' }} fontSize="16px" />
            )}
          </Flex>
        </Flex>
      </Popover>
    </Box>
  );
}
